import type { AnnonceDiffusion } from '~/api/gen'

// Données mock pour GET /diffusions/{officeOid}.
//
// Chaque annonce couvre un cas de l'UI et est exportée nommément : les tests e2e
// s'y réfèrent (`annonceVente.ref_client`…) plutôt que par position, le listing
// réordonnant les annonces diffusées avant les non diffusées.
//
// Comme en prod, `active` est false partout : le `status` seul pilote la pastille.
// Aucune diffusion en `erreur` ici — elles vivent dans le scénario `errors`, pour
// que le bandeau « N erreurs en cours » ne s'affiche pas dans le cas nominal.

/** Vente : les 3 partenaires, dont un publié avec le détail de diffusion complet. */
export const annonceVente: AnnonceDiffusion = {
  id: 1,
  office_oid: 'OFFI0000011616',
  ref_client: 'CD-987654-FGH',
  type_transaction: 'achat',
  type_bien: 'appartement',
  liste_type_bien: ['appartement', 't3'],
  annonce_neuf: false,
  localisation: { numeroRue: '12', adresse: '12 rue des Fleurs', codePostal: '31000', commune: 'Toulouse' },
  prix: 320000,
  detail_prix: {
    prix_net_vendeur: 300000,
    honoraires_negociation: 20000,
    charge_negociation: 'acquereur',
  },
  caracteristiques: { surfaceHabitable: 120, surfaceTerrain: 4000, nbChambres: 3, nbPieces: 5 },
  photos: [{ url: '/images/annonces/img-1.jpg' }],
  diffusions: [
    { id: 11, code_produit: 'LEI', active: false, status: 'depublie' },
    {
      id: 12,
      code_produit: 'LBC',
      active: false,
      status: 'publie',
      nb_photos_envoyees: 8,
      date_diffusion: '2026-08-10T09:30:00',
      url_annonce: 'https://www.leboncoin.fr/ad/ventes_immobilieres/0000001',
    },
    { id: 13, code_produit: 'SLG', active: false, status: 'depublie' },
  ],
}

/** Enchères : diffusions partielles (pas de SeLoger) et pas de détail de prix. */
export const annonceEncheres: AnnonceDiffusion = {
  id: 2,
  office_oid: 'OFFI0000011616',
  ref_client: 'AB-123456-XYZ',
  type_transaction: 'encheres_en_ligne',
  type_bien: 'maison',
  liste_type_bien: ['maison'],
  annonce_neuf: false,
  localisation: { numeroRue: '3', adresse: '3 place Latreille', codePostal: '33000', commune: 'Bordeaux' },
  prix: 450000,
  caracteristiques: { surfaceHabitable: 95, surfaceTerrain: 600, nbChambres: 2, nbPieces: 4 },
  photos: [{ url: '/images/annonces/img-1.jpg' }],
  diffusions: [
    { id: 21, code_produit: 'LEI', active: false, status: 'publie' },
    { id: 22, code_produit: 'LBC', active: false, status: 'depublie' },
  ],
}

/** Aucune diffusion : la carte est affichée mais désactivée. */
export const annonceSansDiffusion: AnnonceDiffusion = {
  id: 3,
  office_oid: 'OFFI0000011616',
  ref_client: 'EF-555555-QRS',
  type_transaction: 'achat',
  type_bien: 'terrain_a_batir',
  liste_type_bien: ['terrain_a_batir'],
  annonce_neuf: false,
  localisation: { numeroRue: '7', adresse: '7 chemin des Vignes', codePostal: '44000', commune: 'Nantes' },
  prix: 98000,
  caracteristiques: { surfaceTerrain: 850 },
  photos: [{ url: '/images/annonces/img-1.jpg' }],
  diffusions: [],
}

/** Location : unique diffusion « en cours » du jeu nominal, détail de prix locatif. */
export const annonceLocation: AnnonceDiffusion = {
  id: 4,
  office_oid: 'OFFI0000011616',
  ref_client: 'GH-777777-JKL',
  type_transaction: 'location',
  type_bien: 'appartement',
  liste_type_bien: ['appartement', 't2'],
  annonce_neuf: false,
  localisation: { numeroRue: '21', adresse: '21 avenue Thiers', codePostal: '35000', commune: 'Rennes' },
  prix: 780,
  detail_prix: {
    loyer_mensuel_hors_charges: 700,
    montant_charges: 80,
    depot_garantie: 780,
  },
  caracteristiques: { surfaceHabitable: 45, nbChambres: 1, nbPieces: 2 },
  photos: [{ url: '/images/annonces/img-1.jpg' }],
  diffusions: [
    { id: 41, code_produit: 'LEI', active: false, status: 'en_cours' },
  ],
}

export const annoncesDiffusion: AnnonceDiffusion[] = [
  annonceVente,
  annonceEncheres,
  annonceSansDiffusion,
  annonceLocation,
]

// Scénario E2E « errors » (cf. ~/msw/scenarios.ts) : annonces présentant des
// diffusions en erreur, pour le bandeau « N erreurs en cours » et les relances.
/** Deux partenaires en erreur, dont un avec le message remonté par le diffuseur. */
export const annonceDeuxErreurs: AnnonceDiffusion = {
  id: 101,
  office_oid: 'OFFI0000011616',
  ref_client: 'ERR-001-TOULOUSE',
  type_transaction: 'achat',
  type_bien: 'appartement',
  liste_type_bien: ['appartement'],
  annonce_neuf: false,
  localisation: { numeroRue: '8', adresse: '8 rue du Taur', codePostal: '31000', commune: 'Toulouse' },
  prix: 250000,
  caracteristiques: { surfaceHabitable: 80, surfaceTerrain: 0, nbChambres: 2, nbPieces: 3 },
  photos: [{ url: '/images/annonces/img-1.jpg' }],
  diffusions: [
    { id: 1011, code_produit: 'LEI', active: false, status: 'erreur', message_erreur: 'Quota du pack atteint', date_diffusion: '2026-08-09T14:12:00' },
    { id: 1012, code_produit: 'LBC', active: false, status: 'erreur' },
    { id: 1013, code_produit: 'SLG', active: false, status: 'publie' },
  ],
}

/** Un seul partenaire en erreur. */
export const annonceUneErreur: AnnonceDiffusion = {
  id: 102,
  office_oid: 'OFFI0000011616',
  ref_client: 'ERR-002-LYON',
  type_transaction: 'location',
  type_bien: 'maison',
  liste_type_bien: ['maison'],
  annonce_neuf: false,
  localisation: { numeroRue: '5', adresse: '5 quai Saint-Vincent', codePostal: '69001', commune: 'Lyon' },
  prix: 510000,
  caracteristiques: { surfaceHabitable: 110, surfaceTerrain: 200, nbChambres: 3, nbPieces: 5 },
  photos: [{ url: '/images/annonces/img-1.jpg' }],
  diffusions: [
    { id: 1021, code_produit: 'LEI', active: false, status: 'erreur' },
    { id: 1022, code_produit: 'LBC', active: false, status: 'publie' },
    { id: 1023, code_produit: 'SLG', active: false, status: 'publie' },
  ],
}

export const annoncesDiffusionAvecErreurs: AnnonceDiffusion[] = [
  annonceDeuxErreurs,
  annonceUneErreur,
]

// Listing de l'office `officeMartin` (cf. ~/msw/mocks/organisation) : ce que la
// hotline voit après l'avoir choisi dans le filtre organisation.

/** Maison de l'Étude Martin, publiée sur LEI. */
export const annonceOfficeMartin: AnnonceDiffusion = {
  id: 21,
  office_oid: 'OFFI0000022727',
  ref_client: 'MA-202020-RTY',
  type_transaction: 'achat',
  type_bien: 'maison',
  liste_type_bien: ['maison'],
  annonce_neuf: false,
  localisation: { numeroRue: '4', adresse: '4 rue de Brest', codePostal: '35000', commune: 'Rennes' },
  prix: 385000,
  caracteristiques: { surfaceHabitable: 120, nbChambres: 4, nbPieces: 6 },
  photos: [{ url: '/images/annonces/img-1.jpg' }],
  diffusions: [
    { id: 211, code_produit: 'LEI', active: false, status: 'publie' },
  ],
}

export const annoncesDiffusionOfficeMartin: AnnonceDiffusion[] = [annonceOfficeMartin]
